(() => {
  'use strict';

  const AA = {
    product: null,
    timer: null,
    currentColour: '',
    activeIndex: 0,

    /*
     * Prevent old asynchronous image loads
     * from overwriting a newer selection.
     */
    buildToken: 0,
    imageToken: 0,

    /*
     * Cache successfully preloaded image URLs.
     */
    loadedImages: new Set(),

    normalise(value = '') {
      return String(value)
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[’']/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },

    getProductInfo() {
      return (
        document.querySelector("product-info[id^='MainProduct']") ||
        document.querySelector('product-info')
      );
    },

    getOriginalGallery() {
      return (
        this.getProductInfo()?.querySelector('media-gallery') ||
        document.querySelector('media-gallery')
      );
    },

    getCustomGallery() {
      return document.querySelector(
        '.aa-colour-gallery'
      );
    },

    getVariantPicker() {
      return (
        this.getProductInfo()?.querySelector('variant-selects') ||
        this.getProductInfo()?.querySelector('variant-radios') ||
        document.querySelector('variant-selects') ||
        document.querySelector('variant-radios')
      );
    },

    getOptionNameFromControl(control) {
      if (!control) return '';

      if (control.matches('select')) {
        const wrapper =
          control.closest(
            '.product-form__input--dropdown'
          );

        const label =
          wrapper?.querySelector('label');

        return this.normalise(
          label?.textContent ||
          control.dataset.optionName ||
          control.name ||
          ''
        );
      }

      const fieldset =
        control.closest('fieldset');

      if (fieldset) {
        const legend =
          fieldset.querySelector('legend');

        return this.normalise(
          legend?.textContent ||
          control.name ||
          ''
        );
      }

      return this.normalise(
        control.name ||
        control.dataset.optionName ||
        ''
      );
    },

    isColourControl(control) {
      const optionName =
        this.getOptionNameFromControl(
          control
        );

      return (
        optionName.includes('color') ||
        optionName.includes('colour')
      );
    },

    getSelectedColour() {
      const picker =
        this.getVariantPicker();

      if (!picker) return '';

      /*
       * RADIO / SWATCH / BUTTON PICKERS
       */
      const fieldsets =
        picker.querySelectorAll(
          'fieldset'
        );

      for (const fieldset of fieldsets) {
        const legend =
          fieldset.querySelector(
            'legend'
          );

        const optionName =
          this.normalise(
            legend?.textContent || ''
          );

        if (
          optionName.includes('color') ||
          optionName.includes('colour')
        ) {
          const checked =
            fieldset.querySelector(
              'input[type="radio"]:checked'
            );

          if (checked?.value) {
            return checked.value.trim();
          }

          const selectedValue =
            fieldset.querySelector(
              '[data-selected-value]'
            );

          if (
            selectedValue
              ?.textContent
              ?.trim()
          ) {
            return selectedValue
              .textContent
              .trim();
          }
        }
      }

      /*
       * DROPDOWN PICKERS
       */
      const dropdowns =
        picker.querySelectorAll(
          '.product-form__input--dropdown'
        );

      for (const wrapper of dropdowns) {
        const label =
          wrapper.querySelector(
            'label'
          );

        const optionName =
          this.normalise(
            label?.textContent || ''
          );

        if (
          optionName.includes('color') ||
          optionName.includes('colour')
        ) {
          const select =
            wrapper.querySelector(
              'select'
            );

          if (select?.value) {
            return select.value.trim();
          }
        }
      }

      return '';
    },

    getAllColours() {
      const picker =
        this.getVariantPicker();

      const colours =
        new Map();

      if (!picker) {
        return [];
      }

      /*
       * Radio / button values.
       */
      picker
        .querySelectorAll(
          'fieldset'
        )
        .forEach(
          (fieldset) => {
            const legend =
              fieldset.querySelector(
                'legend'
              );

            const optionName =
              this.normalise(
                legend?.textContent ||
                ''
              );

            if (
              !optionName.includes(
                'color'
              ) &&
              !optionName.includes(
                'colour'
              )
            ) {
              return;
            }

            fieldset
              .querySelectorAll(
                'input[type="radio"]'
              )
              .forEach(
                (input) => {
                  if (
                    !input.value
                  ) {
                    return;
                  }

                  const slug =
                    this.normalise(
                      input.value
                    );

                  if (slug) {
                    colours.set(
                      slug,
                      input.value.trim()
                    );
                  }
                }
              );
          }
        );

      /*
       * Dropdown values.
       */
      picker
        .querySelectorAll(
          '.product-form__input--dropdown'
        )
        .forEach(
          (wrapper) => {
            const label =
              wrapper.querySelector(
                'label'
              );

            const optionName =
              this.normalise(
                label?.textContent ||
                ''
              );

            if (
              !optionName.includes(
                'color'
              ) &&
              !optionName.includes(
                'colour'
              )
            ) {
              return;
            }

            wrapper
              .querySelectorAll(
                'option'
              )
              .forEach(
                (option) => {
                  if (
                    !option.value
                  ) {
                    return;
                  }

                  const slug =
                    this.normalise(
                      option.value
                    );

                  if (slug) {
                    colours.set(
                      slug,
                      option.value.trim()
                    );
                  }
                }
              );
          }
        );

      /*
       * Longest colour names first.
       * Prevents names like "Heather"
       * accidentally matching before
       * "Heather Navy".
       */
      return Array
        .from(
          colours.entries()
        )
        .map(
          ([slug, original]) => ({
            slug,
            original
          })
        )
        .sort(
          (a, b) =>
            b.slug.length -
            a.slug.length
        );
    },

    /* =========================================================
       COMMON IMAGE
       ========================================================= */

    isCommon(
      alt = '',
      filename = ''
    ) {
      const altText =
        String(
          alt || ''
        );

      return (
        /\[\s*common\s*\]/i.test(
          altText
        ) ||
        /\[\s*common\s*:\s*[^\]]+\]/i.test(
          altText
        ) ||
        /AA-COMMON/i.test(
          altText
        ) ||
        /(?:^|-)aa-common(?:-|\.|$)/i.test(
          filename
        )
      );
    },

    getCommonKey(
      alt = '',
      filename = '',
      src = ''
    ) {
      const altText =
        String(
          alt || ''
        );

      const bracketMatch =
        altText.match(
          /\[\s*common(?:\s*:\s*([^\]]+))?\s*\]/i
        );

      if (bracketMatch) {
        if (
          bracketMatch[1] &&
          bracketMatch[1].trim()
        ) {
          return this.normalise(
            bracketMatch[1]
          );
        }

        return (
          filename ||
          src ||
          'common'
        );
      }

      const legacyMatch =
        altText.match(
          /AA-COMMON(?:\s*:\s*([^|]+))?/i
        );

      if (legacyMatch) {
        if (
          legacyMatch[1] &&
          legacyMatch[1].trim()
        ) {
          return this.normalise(
            legacyMatch[1]
          );
        }

        return (
          filename ||
          src ||
          'common'
        );
      }

      return (
        filename ||
        src ||
        'common'
      );
    },

    /* =========================================================
       SHOPIFY PRODUCT JSON
       ========================================================= */

    async loadProduct() {
      if (this.product) {
        return this.product;
      }

      const url =
        window.location.pathname
          .replace(
            /\/$/,
            ''
          ) +
        '.js';

      const response =
        await fetch(
          url,
          {
            credentials:
              'same-origin'
          }
        );

      if (
        !response.ok
      ) {
        throw new Error(
          'Could not load Shopify product data.'
        );
      }

      this.product =
        await response.json();

      return this.product;
    },

    /* =========================================================
       STYLES
       ========================================================= */

    addStyles() {
      if (
        document.getElementById(
          'aa-colour-gallery-styles'
        )
      ) {
        return;
      }

      const style =
        document.createElement(
          'style'
        );

      style.id =
        'aa-colour-gallery-styles';

      style.textContent = `

        /*
         * Keep Dawn mounted because Shopify's
         * product JavaScript still expects it.
         */
        media-gallery.aa-original-gallery-hidden {
          position: absolute !important;
          left: -100000px !important;
          top: 0 !important;
          width: 1px !important;
          height: 1px !important;
          overflow: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }

        .aa-colour-gallery {
          display: block;
          width: 100%;
          max-width: 100%;
        }

        /*
         * New galleries are prepared invisibly
         * before replacing the current gallery.
         */
        .aa-colour-gallery--preparing {
          position: absolute !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }

        .aa-colour-gallery__stage {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: #ffffff;
        }

        .aa-colour-gallery__main {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
        }

        .aa-colour-gallery__main-image {
          display: block;
          width: 100%;
          height: auto;
          max-width: 100%;
          object-fit: contain;
          background: #ffffff;

          /*
           * Very small fade only AFTER
           * replacement image is loaded.
           */
          opacity: 1;
          transition: opacity 0.12s ease;
        }

        .aa-colour-gallery__main-image.aa-image-switching {
          opacity: 0.88;
        }

        .aa-colour-gallery__arrow {
          appearance: none;
          -webkit-appearance: none;

          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;

          width: 42px;
          height: 42px;

          display: flex;
          align-items: center;
          justify-content: center;

          border: 1px solid rgba(
            197,
            161,
            90,
            0.65
          );

          border-radius: 999px;

          background: rgba(
            0,
            0,
            0,
            0.72
          );

          color: #c5a15a;

          cursor: pointer;

          font-size: 24px;
          line-height: 1;
        }

        .aa-colour-gallery__arrow:hover {
          border-color: #c5a15a;
          color: #e3dac9;
        }

        .aa-colour-gallery__arrow--prev {
          left: 10px;
        }

        .aa-colour-gallery__arrow--next {
          right: 10px;
        }

        .aa-colour-gallery__thumbs {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;

          gap: 12px;
          margin-top: 16px;
        }

        .aa-colour-gallery__thumb {
          appearance: none;
          -webkit-appearance: none;

          display: block;

          width: 92px;
          height: 92px;

          margin: 0;
          padding: 0;

          overflow: hidden;
          cursor: pointer;

          background: #ffffff;

          border: 1px solid rgba(
            197,
            161,
            90,
            0.55
          );
        }

        .aa-colour-gallery__thumb img {
          display: block;

          width: 100%;
          height: 100%;

          object-fit: cover;

          background: #ffffff;
        }

        .aa-colour-gallery__thumb[
          aria-current="true"
        ] {
          border: 2px solid #c5a15a;
        }

        .aa-colour-gallery__counter {
          display: block;

          margin-top: 10px;

          text-align: center;
          font-size: 1.2rem;
        }

        @media screen and (max-width: 749px) {

          .aa-colour-gallery__thumbs {
            gap: 8px;
          }

          .aa-colour-gallery__thumb {
            width: 72px;
            height: 72px;
          }

          .aa-colour-gallery__arrow {
            width: 36px;
            height: 36px;

            font-size: 20px;
          }
        }
      `;

      document.head.appendChild(
        style
      );
    },

    /* =========================================================
       MEDIA DATA
       ========================================================= */

    getImageSource(media) {
      return (
        media?.src ||
        media?.preview_image?.src ||
        ''
      );
    },

    getFilename(
      src = ''
    ) {
      if (!src) {
        return '';
      }

      try {
        return decodeURIComponent(
          String(src)
            .split('?')[0]
            .split('/')
            .pop() ||
          ''
        )
          .toLowerCase()
          .replace(
            /_/g,
            '-'
          );
      } catch (error) {
        return String(src)
          .toLowerCase()
          .replace(
            /_/g,
            '-'
          );
      }
    },

    detectMediaColour(
      filename,
      colours
    ) {
      if (
        !filename ||
        !colours.length
      ) {
        return '';
      }

      const clean =
        filename
          .replace(
            /\.[a-z0-9]+$/i,
            ''
          )
          .replace(
            /[^a-z0-9]+/g,
            '-'
          )
          .replace(
            /^-+|-+$/g,
            ''
          );

      for (
        const colour
        of colours
      ) {
        const slug =
          colour.slug;

        if (!slug) {
          continue;
        }

        const pattern =
          new RegExp(
            `(?:^|-)${slug}(?:-|$)`,
            'i'
          );

        if (
          pattern.test(
            clean
          )
        ) {
          return slug;
        }
      }

      return '';
    },

    getMediaForColour(
      selectedColour
    ) {
      if (
        !this.product
      ) {
        return [];
      }

      const selectedSlug =
        this.normalise(
          selectedColour
        );

      const colours =
        this.getAllColours();

      const allMedia =
        (
          this.product.media ||
          []
        )
          .filter(
            (media) => {
              return (
                !media.media_type ||
                media.media_type ===
                  'image'
              );
            }
          )
          .map(
            (
              media,
              index
            ) => {
              const alt =
                media.alt ||
                '';

              const src =
                this.getImageSource(
                  media
                );

              const filename =
                this.getFilename(
                  src
                );

              return {
                index,

                id:
                  media.id ||
                  index,

                alt,
                src,
                filename,

                width:
                  media.width ||
                  media
                    .preview_image
                    ?.width ||
                  '',

                height:
                  media.height ||
                  media
                    .preview_image
                    ?.height ||
                  '',

                colour:
                  this.detectMediaColour(
                    filename,
                    colours
                  ),

                common:
                  this.isCommon(
                    alt,
                    filename
                  ),

                commonKey:
                  this.getCommonKey(
                    alt,
                    filename,
                    src
                  )
              };
            }
          )
          .filter(
            media =>
              media.src
          );

      /*
       * Common images must NOT accidentally
       * be counted as colour images.
       */
      const colourImages =
        allMedia.filter(
          media =>
            !media.common &&
            media.colour ===
              selectedSlug
        );

      if (
        !colourImages.length
      ) {
        return [];
      }

      const commonSeen =
        new Set();

      const commonImages =
        allMedia.filter(
          media => {
            if (
              !media.common
            ) {
              return false;
            }

            const key =
              media.commonKey ||
              media.src;

            if (
              commonSeen.has(
                key
              )
            ) {
              return false;
            }

            commonSeen.add(
              key
            );

            return true;
          }
        );

      /*
       * Keep Shopify order inside each group:
       *
       * Colour image 1
       * Colour image 2
       * Colour image 3
       * Colour image 4
       * Common size guide
       */
      colourImages.sort(
        (a, b) =>
          a.index -
          b.index
      );

      commonImages.sort(
        (a, b) =>
          a.index -
          b.index
      );

      return [
        ...colourImages,
        ...commonImages
      ];
    },

    /* =========================================================
       IMAGE PRELOADING
       ========================================================= */

    preloadImage(
      src
    ) {
      if (!src) {
        return Promise.resolve(
          false
        );
      }

      if (
        this.loadedImages.has(
          src
        )
      ) {
        return Promise.resolve(
          true
        );
      }

      return new Promise(
        (resolve) => {
          const image =
            new Image();

          let finished =
            false;

          const done =
            (result) => {
              if (
                finished
              ) {
                return;
              }

              finished =
                true;

              if (
                result
              ) {
                this.loadedImages.add(
                  src
                );
              }

              resolve(
                result
              );
            };

          image.onload =
            async () => {
              /*
               * decode() prevents the browser from
               * reporting loaded before the image
               * can actually be painted.
               */
              try {
                if (
                  typeof image.decode ===
                  'function'
                ) {
                  await image.decode();
                }
              } catch (
                error
              ) {
                /*
                 * Image is still usable even when
                 * decode() isn't supported.
                 */
              }

              done(
                true
              );
            };

          image.onerror =
            () => {
              done(
                false
              );
            };

          image.src =
            src;

          /*
           * Never lock the gallery forever
           * if Shopify CDN stalls.
           */
          window.setTimeout(
            () => {
              done(
                image.complete &&
                image.naturalWidth >
                  0
              );
            },
            5000
          );
        }
      );
    },

    async preloadMedia(
      mediaItems
    ) {
      if (
        !mediaItems.length
      ) {
        return;
      }

      /*
       * MAIN IMAGE FIRST.
       * This is the one that prevents
       * the white flash.
       */
      await this.preloadImage(
        mediaItems[0].src
      );

      /*
       * Remaining gallery images preload
       * in parallel afterwards.
       */
      Promise.allSettled(
        mediaItems
          .slice(1)
          .map(
            media =>
              this.preloadImage(
                media.src
              )
          )
      );
    },

    createImage(
      media,
      className = ''
    ) {
      const image =
        document.createElement(
          'img'
        );

      image.className =
        className;

      image.alt =
        media.alt ||
        '';

      /*
       * Width/height stabilise the gallery
       * while images load.
       */
      if (
        media.width
      ) {
        image.width =
          media.width;
      }

      if (
        media.height
      ) {
        image.height =
          media.height;
      }

      image.decoding =
        'async';

      /*
       * Source assigned only after preload.
       */
      image.src =
        media.src;

      return image;
    },

    /* =========================================================
       GALLERY CLEANUP
       ========================================================= */

    removeCustomGallery(
      except = null
    ) {
      document
        .querySelectorAll(
          '.aa-colour-gallery'
        )
        .forEach(
          element => {
            if (
              except &&
              element ===
                except
            ) {
              return;
            }

            element.remove();
          }
        );
    },

    restoreOriginalGallery() {
      this.removeCustomGallery();

      const original =
        this.getOriginalGallery();

      if (
        original
      ) {
        original
          .classList
          .remove(
            'aa-original-gallery-hidden'
          );
      }

      this.currentColour =
        '';

      this.activeIndex =
        0;
    },

    /* =========================================================
       BUILD GALLERY — NO WHITE FLASH
       ========================================================= */

    async buildGallery(
      force = false
    ) {
      const buildToken =
        ++this.buildToken;

      const original =
        this.getOriginalGallery();

      if (
        !original ||
        !this.product
      ) {
        return;
      }

      const selectedColour =
        this.getSelectedColour();

      const normalizedColour =
        this.normalise(
          selectedColour
        );

      if (
        !normalizedColour
      ) {
        return;
      }

      /*
       * Don't rebuild the same colour
       * unnecessarily.
       */
      if (
        !force &&
        this.currentColour ===
          normalizedColour &&
        this.getCustomGallery()
      ) {
        return;
      }

      const mediaItems =
        this.getMediaForColour(
          selectedColour
        );

      if (
        !mediaItems.length
      ) {
        /*
         * Keep whichever gallery is already
         * visible instead of flashing blank.
         */
        if (
          !this.getCustomGallery()
        ) {
          this.restoreOriginalGallery();
        }

        return;
      }

      /*
       * CRITICAL:
       *
       * Load the new main image BEFORE
       * removing the existing gallery.
       */
      const firstLoaded =
        await this.preloadImage(
          mediaItems[0].src
        );

      /*
       * User may have selected another
       * colour while we were loading.
       */
      if (
        buildToken !==
          this.buildToken
      ) {
        return;
      }

      if (
        !firstLoaded
      ) {
        return;
      }

      /*
       * Start preloading remaining images.
       */
      Promise.allSettled(
        mediaItems
          .slice(1)
          .map(
            media =>
              this.preloadImage(
                media.src
              )
          )
      );

      this.activeIndex =
        0;

      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.className =
        'aa-colour-gallery aa-colour-gallery--preparing';

      wrapper.dataset.aaColour =
        normalizedColour;

      const stage =
        document.createElement(
          'div'
        );

      stage.className =
        'aa-colour-gallery__stage';

      const main =
        document.createElement(
          'div'
        );

      main.className =
        'aa-colour-gallery__main';

      const mainImage =
        this.createImage(
          mediaItems[0],
          'aa-colour-gallery__main-image'
        );

      mainImage.loading =
        'eager';

      mainImage.fetchPriority =
        'high';

      main.appendChild(
        mainImage
      );

      stage.appendChild(
        main
      );

      const thumbs =
        document.createElement(
          'div'
        );

      thumbs.className =
        'aa-colour-gallery__thumbs';

      const counter =
        document.createElement(
          'div'
        );

      counter.className =
        'aa-colour-gallery__counter';

      const buttons =
        [];

      /*
       * Switching gallery image also waits
       * until the target has loaded.
       */
      const setActive =
        async (
          index
        ) => {
          if (
            !mediaItems.length
          ) {
            return;
          }

          const safeIndex =
            (
              index +
              mediaItems.length
            ) %
            mediaItems.length;

          if (
            safeIndex ===
              this.activeIndex
          ) {
            return;
          }

          const media =
            mediaItems[
              safeIndex
            ];

          const imageToken =
            ++this.imageToken;

          /*
           * KEEP CURRENT IMAGE VISIBLE.
           */
          const loaded =
            await this.preloadImage(
              media.src
            );

          if (
            !loaded ||
            imageToken !==
              this.imageToken
          ) {
            return;
          }

          mainImage.classList.add(
            'aa-image-switching'
          );

          /*
           * Because src is already cached,
           * this change is effectively instant.
           */
          mainImage.src =
            media.src;

          mainImage.alt =
            media.alt ||
            '';

          this.activeIndex =
            safeIndex;

          buttons.forEach(
            (
              button,
              buttonIndex
            ) => {
              button.setAttribute(
                'aria-current',

                buttonIndex ===
                  safeIndex
                  ? 'true'
                  : 'false'
              );
            }
          );

          counter.textContent =
            `${safeIndex + 1} / ${mediaItems.length}`;

          requestAnimationFrame(
            () => {
              requestAnimationFrame(
                () => {
                  mainImage
                    .classList
                    .remove(
                      'aa-image-switching'
                    );
                }
              );
            }
          );
        };

      mediaItems.forEach(
        (
          media,
          index
        ) => {
          const button =
            document.createElement(
              'button'
            );

          button.type =
            'button';

          button.className =
            'aa-colour-gallery__thumb';

          button.setAttribute(
            'aria-label',
            `View image ${index + 1}`
          );

          button.setAttribute(
            'aria-current',
            index === 0
              ? 'true'
              : 'false'
          );

          const thumbnail =
            this.createImage(
              media
            );

          thumbnail.loading =
            index < 4
              ? 'eager'
              : 'lazy';

          button.appendChild(
            thumbnail
          );

          button.addEventListener(
            'click',
            () => {
              setActive(
                index
              );
            }
          );

          buttons.push(
            button
          );

          thumbs.appendChild(
            button
          );
        }
      );

      counter.textContent =
        `1 / ${mediaItems.length}`;

      /*
       * Previous / Next.
       */
      if (
        mediaItems.length >
          1
      ) {
        const prev =
          document.createElement(
            'button'
          );

        prev.type =
          'button';

        prev.className =
          'aa-colour-gallery__arrow aa-colour-gallery__arrow--prev';

        prev.setAttribute(
          'aria-label',
          'Previous image'
        );

        prev.innerHTML =
          '&#8249;';

        prev.addEventListener(
          'click',
          () => {
            setActive(
              this.activeIndex -
                1
            );
          }
        );

        const next =
          document.createElement(
            'button'
          );

        next.type =
          'button';

        next.className =
          'aa-colour-gallery__arrow aa-colour-gallery__arrow--next';

        next.setAttribute(
          'aria-label',
          'Next image'
        );

        next.innerHTML =
          '&#8250;';

        next.addEventListener(
          'click',
          () => {
            setActive(
              this.activeIndex +
                1
            );
          }
        );

        stage.appendChild(
          prev
        );

        stage.appendChild(
          next
        );
      }

      wrapper.appendChild(
        stage
      );

      if (
        mediaItems.length >
          1
      ) {
        wrapper.appendChild(
          thumbs
        );

        wrapper.appendChild(
          counter
        );
      }

      /*
       * Insert NEW gallery while OLD gallery
       * is still visible.
       */
      original.parentNode.insertBefore(
        wrapper,
        original
      );

      /*
       * The main image is already loaded,
       * so reveal the new gallery now.
       */
      wrapper.classList.remove(
        'aa-colour-gallery--preparing'
      );

      /*
       * Only NOW remove the previous custom
       * gallery.
       */
      this.removeCustomGallery(
        wrapper
      );

      /*
       * Hide Dawn only once our replacement
       * is safely visible.
       */
      original.classList.add(
        'aa-original-gallery-hidden'
      );

      this.currentColour =
        normalizedColour;
    },

    schedule(
      delay = 100,
      force = false
    ) {
      clearTimeout(
        this.timer
      );

      this.timer =
        window.setTimeout(
          () => {
            this.buildGallery(
              force
            );
          },
          delay
        );
    },

    refresh(
      force = true
    ) {
      this.currentColour =
        '';

      /*
       * Cancel any earlier asynchronous
       * image swap.
       */
      this.imageToken++;

      this.schedule(
        30,
        force
      );

      /*
       * Dawn sometimes completes its own
       * variant work afterwards.
       */
      window.setTimeout(
        () => {
          this.buildGallery(
            force
          );
        },
        180
      );

      window.setTimeout(
        () => {
          this.buildGallery(
            force
          );
        },
        420
      );
    },

    bindEvents() {
      /*
       * Only rebuild when COLOUR changes.
       * Size changes should never cause
       * the product images to flash.
       */
      document.addEventListener(
        'change',
        event => {
          const control =
            event.target;

          if (
            !control
              ?.closest?.(
                'variant-selects, variant-radios'
              )
          ) {
            return;
          }

          if (
            !this.isColourControl(
              control
            )
          ) {
            return;
          }

          this.refresh(
            true
          );
        },
        true
      );

      /*
       * Shopify Theme Editor.
       */
      document.addEventListener(
        'shopify:section:load',
        () => {
          this.refresh(
            true
          );
        }
      );

      /*
       * Back / forward browser navigation.
       */
      window.addEventListener(
        'popstate',
        () => {
          this.refresh(
            true
          );
        }
      );
    },

    /*
     * Preload current colour and then
     * quietly warm likely gallery images.
     */
    async warmInitialImages() {
      const colour =
        this.getSelectedColour();

      if (
        !colour
      ) {
        return;
      }

      const media =
        this.getMediaForColour(
          colour
        );

      await this.preloadMedia(
        media
      );
    },

    async init() {
      try {
        this.addStyles();

        await this.loadProduct();

        this.bindEvents();

        /*
         * Load current product image before
         * replacing Dawn's gallery.
         */
        await this.warmInitialImages();

        this.schedule(
          20,
          true
        );

        window.setTimeout(
          () => {
            this.buildGallery(
              true
            );
          },
          250
        );

      } catch (
        error
      ) {
        console.error(
          'AA Colour Gallery:',
          error
        );

        this.restoreOriginalGallery();
      }
    }
  };

  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        AA.init();
      }
    );
  } else {
    AA.init();
  }

  window.AAColourGallery =
    AA;
})();